// src/components/SetupPage.jsx
import React from 'react';
import { Routes, Route, Navigate, NavLink, useLocation, useParams } from 'react-router-dom';
import { ASSET_SETUP_TABS, DEFAULT_SETUP_TAB_PATH } from '../constants/setupTabs';

import TeachersPage from './TeachersPage';
import TeacherDetailPage from './TeacherDetailPage';
import GroupsPage from './GroupsPage';
import RoomsPage from './RoomsPage';
import RoomDetailPage from './RoomDetailPage';
import SubjectsPage from './SubjectsPage.tsx';
import GroupDetailPage from './GroupDetailPage';
import ClassSchedulerPage from './ClassSchedulerPage';
import SchoolSettingsPage from './SchoolSettingsPage';
import YearStructureEditor from './YearStructureEditor';

// Accept user prop
function SetupPage({
    user,
    teachers,
    onAddTeacher,
    onUpdateTeacher,
    onDeleteTeacher,
    groups,
    onAddGroup,
    onUpdateGroup,
    onDeleteGroup,
    onAddStudentToGroup,
    onAddStudentsToGroupBatch,
    onRemoveStudentFromGroup,
    onAddNewClass,
    rooms,
    onAddRoom,
    onUpdateRoom,
    onDeleteRoom,
    subjects,
}) {
    const location = useLocation();
    const showAssetSetupNav = location.pathname === '/setup'
        || ASSET_SETUP_TABS.some((tab) => location.pathname === tab.path || location.pathname.startsWith(`${tab.path}/`));

    const getSetupNavLinkClass = ({ isActive }) => {
        return `whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${isActive ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-blue-50 hover:text-blue-700'}`;
    };

    return (
        <div className="bg-gray-100 min-h-screen">
            {showAssetSetupNav && (
                <div className="sticky top-16 z-30 border-b border-gray-200 bg-white/95 backdrop-blur">
                    <div className="px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col gap-3 py-3 md:flex-row md:items-center md:justify-between">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Setup</p>
                                <h1 className="text-lg font-semibold text-gray-900">School Assets</h1>
                            </div>
                            <nav className="flex gap-2 overflow-x-auto pb-1 md:pb-0" aria-label="Setup sections">
                                {ASSET_SETUP_TABS.map((tab) => (
                                    <NavLink
                                        key={tab.id}
                                        to={tab.path}
                                        className={getSetupNavLinkClass}
                                        title={tab.tooltip}
                                    >
                                        {tab.label}
                                    </NavLink>
                                ))}
                            </nav>
                        </div>
                    </div>
                </div>
            )}
            <div className="p-4 md:p-6 lg:p-8">
                <Routes>
                    {/* School Settings Route */}
                    <Route
                        path="school-settings"
                        element={<SchoolSettingsPage user={user} />}
                    />

                    {/* Teachers List Route */}
                    <Route
                        path="teachers"
                        element={<TeachersPage teachers={teachers} onAddTeacher={onAddTeacher} />}
                    />
                    {/* Teacher Detail Route */}
                    <Route
                        path="teachers/:teacherId"
                        element={
                            <TeacherDetailPage
                                user={user}
                                onUpdateTeacher={onUpdateTeacher}
                                onDeleteTeacher={onDeleteTeacher}
                            />
                        }
                    />

                    {/* Groups List Route */}
                    <Route
                        path="groups"
                        element={<GroupsPage groups={groups} onAddGroup={onAddGroup} />}
                    />
                    {/* Group Detail Route */}
                    <Route
                        path="groups/:groupId"
                        element={
                            <GroupDetailPageWrapper
                                user={user}
                                groups={groups}
                                teachers={teachers}
                                subjects={subjects}
                                rooms={rooms}
                                onUpdateGroup={onUpdateGroup}
                                onDeleteGroup={onDeleteGroup}
                                onAddStudentToGroup={onAddStudentToGroup}
                                onAddStudentsToGroupBatch={onAddStudentsToGroupBatch}
                                onRemoveStudentFromGroup={onRemoveStudentFromGroup}
                                onAddNewClass={onAddNewClass}
                            />
                        }
                    />

                    {/* Rooms Route */}
                    <Route
                        path="rooms"
                        element={
                            <RoomsPage
                                rooms={rooms}
                                onAddRoom={onAddRoom}
                                onUpdateRoom={onUpdateRoom}
                                onDeleteRoom={onDeleteRoom}
                            />
                        }
                    />
                    {/* Room Detail Route */}
                    <Route
                        path="rooms/:roomId"
                        element={
                            <RoomDetailPage
                                user={user}
                                onUpdateRoom={onUpdateRoom}
                                onDeleteRoom={onDeleteRoom}
                            />
                        }
                    />

                    {/* Subjects Route */}
                    <Route path="subjects" element={<SubjectsPage />} />

                    {/* Year Structure Route */}
                    <Route
                        path="year-structure"
                        element={<YearStructureEditor user={user} />}
                    />

                    {/* Class Scheduler Route - MODIFIED TO PASS USER PROP */}
                    <Route
                        path="class-scheduler"
                        element={<ClassSchedulerPage user={user} />} // Pass user to ClassSchedulerPage
                    />

                    {/* Default route: School Settings */}
                    <Route index element={<Navigate to={DEFAULT_SETUP_TAB_PATH} replace />} />
                    <Route path="*" element={<Navigate to={DEFAULT_SETUP_TAB_PATH} replace />} />
                </Routes>
            </div>
        </div>
    );
}

function GroupDetailPageWrapper({
    user,
    groups,
    teachers,
    subjects,
    rooms,
    onUpdateGroup,
    onDeleteGroup,
    onAddStudentToGroup,
    onAddStudentsToGroupBatch,
    onRemoveStudentFromGroup,
    onAddNewClass
}) {
    const { groupId: groupIdFromUrl } = useParams();
    const numericGroupIdFromUrl = parseInt(groupIdFromUrl);

    console.log(`[GroupDetailPageWrapper] Rendering. Received groups array length: ${groups ? groups.length : 'undefined'}. Target URL groupId: ${groupIdFromUrl} (numeric: ${numericGroupIdFromUrl})`);

    if (groups && groups.length > 0) {
        console.log('[GroupDetailPageWrapper] Groups array contents (first 5 IDs):', groups.slice(0, 5).map(g => g.id));
    }

    const groupToPass = groups ? groups.find(g => g.id === numericGroupIdFromUrl) : undefined;

    console.log(`[GroupDetailPageWrapper] Group finding result - ID: ${numericGroupIdFromUrl}. Found group object:`, groupToPass ? { id: groupToPass.id, name: groupToPass.name, studentsCount: groupToPass.students?.length, prefsCount: groupToPass.preferences?.length } : 'NOT FOUND');

    if (groups && groups.length > 0 && !groupToPass && groupIdFromUrl) {
        console.warn(`[GroupDetailPageWrapper] Group with ID ${numericGroupIdFromUrl} was NOT FOUND in the provided groups list (length: ${groups.length}). URL ID: ${groupIdFromUrl}.`);
    }

    return (
        <GroupDetailPage
            user={user}
            group={groupToPass}
            groups={groups}
            teachers={teachers}
            subjects={subjects}
            rooms={rooms}
            onUpdateGroup={onUpdateGroup}
            onDeleteGroup={onDeleteGroup}
            onAddStudentToGroup={onAddStudentToGroup}
            onAddStudentsToGroupBatch={onAddStudentsToGroupBatch}
            onRemoveStudentFromGroup={onRemoveStudentFromGroup}
            onAddNewClass={onAddNewClass}
        />
    );
}

export default SetupPage;